import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { summarizePersonalLicenses, type AdminLicenseSummary } from '@/lib/admin-license-summary'
import { useAuth } from './useAuth'

/**
 * supabase-js discards the response body on a non-2xx and leaves only a generic
 * "Edge Function returned a non-2xx status code". Our functions put the useful
 * text in `{ error }`, so read it back off the raw response — otherwise real
 * messages like "This user already has an account" never reach the admin.
 */
async function edgeFunctionError(error: unknown, fallback: string): Promise<Error> {
  if (error instanceof FunctionsHttpError) {
    const body = await error.context.json().catch(() => null)
    if (body?.error) return new Error(body.error)
  }
  return new Error(error instanceof Error ? error.message : fallback)
}

// Types
export type UserProfile = {
  id: string
  full_name: string | null
  is_admin: boolean | null
  is_beta_tester: boolean
  is_subscribed_to_product_updates: boolean
}

export type UserLicense = {
  id: string
  user_id: string
  org_id: string | null
  license_tier: string
  total_devices: number
  annual_quota_km2: number
  purchased_at: string
  expires_at: string
  status: string
  created_at: string
  updated_at: string
}

export type DeviceLicense = {
  id: string
  user_license_id: string
  user_id: string
  license_id: string
  hardware_fingerprint: string
  hardware_serial: string | null
  activated_at: string
  last_seen: string | null
  status: string
  key_version: number
}

export type MapRequest = {
  id: number
  user_id: string
  area_name: string
  area_km2: number | null
  status: string
  created_at: string
  quota_year: number | null
}

export type UserWithDetails = UserProfile & {
  email?: string
  licenses: UserLicense[]
  devices: DeviceLicense[]
  maps: MapRequest[]
}

/**
 * Fetch current user's profile to check admin status
 */
async function fetchCurrentUserProfile(userId: string): Promise<UserProfile | null> {
  const { data, error } = await supabase.from('user_profiles').select('*').eq('id', userId).single()

  if (error) {
    console.error('Failed to fetch user profile:', error)
    return null
  }

  return data as UserProfile
}

/**
 * Hook to get current user's profile
 */
export function useCurrentUserProfile() {
  const { user, loading: authLoading } = useAuth()

  return useQuery({
    queryKey: ['admin', 'currentProfile', user?.id],
    enabled: !!user && !authLoading,
    queryFn: () => fetchCurrentUserProfile(user!.id),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })
}

/**
 * Hook to check if current user is admin
 */
export function useIsAdmin(): { isAdmin: boolean; isLoading: boolean } {
  const { data: profile, isLoading } = useCurrentUserProfile()
  return {
    isAdmin: profile?.is_admin === true,
    isLoading,
  }
}

/**
 * User with enriched data for admin table view
 */
export type EnrichedUser = UserProfile & {
  email?: string
  created_at?: string
  last_sign_in_at?: string | null
  email_confirmed_at?: string | null
  active_license?: AdminLicenseSummary | null
  device_count: number
  used_quota_km2: number
}

export type AdminOrganizationOverview = {
  organization_id: string
  organization_name: string
  owner_id: string
  owner_name: string | null
  member_count: number
  active_license_count: number
  license_tiers: string[]
  total_device_slots: number
  active_devices: number
  annual_quota_km2: number
  attributed_used_km2: number
  finite_attributed_used_km2: number
  remaining_km2: number
  over_quota_license_count: number
  legacy_used_km2: number
  legacy_map_count: number
  next_expiration: string | null
}

export type AdminOrganizationMember = {
  id: string
  user_id: string
  role: string
  joined_at: string
}

/**
 * Response from list-admin-users edge function
 */
type ListAdminUsersResponse = {
  success: boolean
  users?: Array<{
    id: string
    email?: string
    full_name: string | null
    is_admin: boolean | null
    is_beta_tester: boolean
    is_subscribed_to_product_updates: boolean
    created_at: string
    last_sign_in_at: string | null
    email_confirmed_at: string | null
  }>
  error?: string
}

/**
 * Get Supabase Functions URL based on environment
 */
function getSupabaseFunctionsUrl(): string {
  const isDev = import.meta.env.MODE === 'development'
  const useProdSupabase = import.meta.env.VITE_USE_PROD_SUPABASE === 'true'
  const useDevConfig = isDev && !useProdSupabase

  const supabaseUrl = useDevConfig ? import.meta.env.VITE_DEV_SUPABASE_URL : import.meta.env.VITE_SUPABASE_URL

  return `${supabaseUrl}/functions/v1`
}

/**
 * Fetch all users with their profiles, licenses, and stats (admin only)
 * Uses edge function to get auth metadata (email, created_at, last_sign_in_at)
 */
async function fetchAllUsers(): Promise<EnrichedUser[]> {
  // Get current session for auth header
  const {
    data: { session },
  } = await supabase.auth.getSession()
  if (!session?.access_token) {
    throw new Error('Not authenticated')
  }

  // Call edge function to get users with auth data
  const functionsUrl = getSupabaseFunctionsUrl()
  const response = await fetch(`${functionsUrl}/list-admin-users`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  })

  const result: ListAdminUsersResponse = await response.json()

  if (!response.ok || !result.success) {
    console.error('Failed to fetch users:', result.error)
    throw new Error(result.error || 'Failed to fetch users')
  }

  const users = result.users || []

  if (users.length === 0) {
    return []
  }

  const userIds = users.map((u) => u.id)

  // Fetch active licenses for all users
  const { data: licenses } = await supabase
    .from('user_licenses')
    .select('id, user_id, license_tier, total_devices, annual_quota_km2, status')
    .in('user_id', userIds)
    .is('org_id', null)
    .in('status', ['active', 'expired'])

  // Fetch only devices attributed to personal licenses. Organization devices are
  // reported in the organization inventory instead of against the activating user.
  const personalLicenseIds = (licenses ?? []).map((license) => license.id)
  const devices = personalLicenseIds.length > 0
    ? (await supabase
        .from('device_licenses')
        .select('user_id, hardware_fingerprint, status')
        .in('user_license_id', personalLicenseIds)
        .in('status', ['active', 'expired'])).data
    : []

  // Fetch completed map requests to calculate used quota
  const { data: maps } = await supabase
    .from('map_requests')
    .select('user_id, area_km2, status')
    .in('user_id', userIds)
    .is('org_id', null)
    .eq('status', 'completed')

  // Build lookup maps
  const licenseByUser = summarizePersonalLicenses(licenses ?? [])

  const deviceCountByUser = new Map<string, number>()
  const countedDevices = new Set<string>()
  for (const device of devices ?? []) {
    const key = `${device.user_id}:${device.hardware_fingerprint}`
    if (countedDevices.has(key)) continue
    countedDevices.add(key)
    deviceCountByUser.set(device.user_id, (deviceCountByUser.get(device.user_id) ?? 0) + 1)
  }

  const quotaByUser = new Map<string, number>()
  for (const map of maps ?? []) {
    quotaByUser.set(map.user_id, (quotaByUser.get(map.user_id) ?? 0) + (map.area_km2 ?? 0))
  }

  // Enrich users with license/device/quota data
  return users.map((user) => ({
    ...user,
    active_license: licenseByUser.get(user.id) ?? null,
    device_count: deviceCountByUser.get(user.id) ?? 0,
    used_quota_km2: quotaByUser.get(user.id) ?? 0,
  })) as EnrichedUser[]
}

/**
 * Hook to fetch all users with enriched data (admin only)
 */
export function useAdminUsers() {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery<EnrichedUser[]>({
    queryKey: ['admin', 'users'],
    enabled: isAdmin && !adminLoading,
    queryFn: fetchAllUsers,
    staleTime: 30 * 1000, // Cache for 30 seconds
  })
}

/** Invite a user directly from Theseus and grant exactly one personal trial. */
export function useInviteUserWithTrial() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke<{
        success: boolean
        error?: string
      }>('invite-user-with-trial', { body: { email } })

      if (error) throw await edgeFunctionError(error, 'Failed to send invitation')
      if (!data?.success) throw new Error(data?.error || 'Failed to send invitation')
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Create the account and return its setup link without emailing it, for a user
 * whose provider is not receiving our mail. The admin delivers the link.
 * Identical to useInviteUserWithTrial otherwise, including the trial grant.
 */
export function useGenerateInviteLink() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke<{
        success: boolean
        invite_url?: string
        error?: string
      }>('invite-user-with-trial', { body: { email, send_email: false } })

      if (error) throw await edgeFunctionError(error, 'Failed to generate link')
      if (!data?.success) throw new Error(data?.error || 'Failed to generate link')
      if (!data.invite_url) throw new Error('No link returned')
      return data.invite_url
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })
}

/**
 * Mint a password reset link for an account that already exists, without
 * emailing it, so an admin can deliver it by hand. Unlike the invite hooks this
 * creates nothing and grants nothing — it only produces the link.
 */
export function useGenerateResetLink() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke<{
        success: boolean
        reset_url?: string
        error?: string
      }>('generate-reset-link', { body: { email } })

      if (error) throw await edgeFunctionError(error, 'Failed to generate reset link')
      if (!data?.success) throw new Error(data?.error || 'Failed to generate reset link')
      if (!data.reset_url) throw new Error('No link returned')
      return data.reset_url
    },
  })
}

/**
 * Fetch all licenses for a specific user (admin only)
 */
async function fetchUserLicenses(userId: string): Promise<UserLicense[]> {
  const { data, error } = await supabase
    .from('user_licenses')
    .select('*')
    .eq('user_id', userId)
    .is('org_id', null)
    .order('purchased_at', { ascending: false })

  if (error) {
    console.error('Failed to fetch user licenses:', error)
    throw new Error('Failed to fetch user licenses')
  }

  return (data ?? []) as UserLicense[]
}

/**
 * Hook to fetch licenses for a specific user (admin only)
 */
export function useUserLicenses(userId: string | null) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'userLicenses', userId],
    enabled: isAdmin && !adminLoading && !!userId,
    queryFn: () => fetchUserLicenses(userId!),
    staleTime: 30 * 1000,
  })
}

/**
 * Fetch all devices for a specific user (admin only)
 */
async function fetchUserDevices(userId: string): Promise<DeviceLicense[]> {
  // Superseded rows are the historical licenses a device sheds each time it is
  // re-flashed. They exist for the database record only: they consume no seat
  // and are not hardware in service, so listing or counting them here would
  // overstate how much hardware a customer has, and would grow with every
  // reflash. Excluded at the query so no consumer can surface them by accident.
  const { data, error } = await supabase
    .from('device_licenses')
    .select('*, user_licenses!inner(org_id)')
    .eq('user_id', userId)
    .is('user_licenses.org_id', null)
    .neq('status', 'superseded')
    .order('activated_at', { ascending: false })

  if (error) {
    console.error('Failed to fetch user devices:', error)
    throw new Error('Failed to fetch user devices')
  }

  return (data ?? []) as DeviceLicense[]
}

/**
 * Hook to fetch devices for a specific user (admin only)
 */
export function useUserDevices(userId: string | null) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'userDevices', userId],
    enabled: isAdmin && !adminLoading && !!userId,
    queryFn: () => fetchUserDevices(userId!),
    staleTime: 30 * 1000,
  })
}

/**
 * Fetch all map requests for a specific user (admin only)
 */
async function fetchUserMaps(userId: string): Promise<MapRequest[]> {
  const { data, error } = await supabase
    .from('map_requests')
    .select('id, user_id, area_name, area_km2, status, created_at, quota_year')
    .eq('user_id', userId)
    .is('org_id', null)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Failed to fetch user maps:', error)
    throw new Error('Failed to fetch user maps')
  }

  return (data ?? []) as MapRequest[]
}

/**
 * Hook to fetch map requests for a specific user (admin only)
 */
export function useUserMaps(userId: string | null) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'userMaps', userId],
    enabled: isAdmin && !adminLoading && !!userId,
    queryFn: () => fetchUserMaps(userId!),
    staleTime: 30 * 1000,
  })
}

async function fetchAdminOrganizations(): Promise<AdminOrganizationOverview[]> {
  const { data, error } = await supabase.rpc('get_admin_organization_license_overview')

  if (error) {
    console.error('Failed to fetch organization license overview:', error)
    throw new Error('Failed to fetch organization license overview')
  }

  return (data ?? []) as AdminOrganizationOverview[]
}

export function useAdminOrganizations() {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'organizations'],
    enabled: isAdmin && !adminLoading,
    queryFn: fetchAdminOrganizations,
    staleTime: 30 * 1000,
  })
}

async function fetchOrganizationLicenses(orgId: string): Promise<UserLicense[]> {
  const { data, error } = await supabase
    .from('user_licenses')
    .select('*')
    .eq('org_id', orgId)
    .order('purchased_at', { ascending: false })

  if (error) throw new Error(`Failed to fetch organization licenses: ${error.message}`)
  return (data ?? []) as UserLicense[]
}

export function useOrganizationLicenses(orgId: string | null) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'organizationLicenses', orgId],
    enabled: isAdmin && !adminLoading && !!orgId,
    queryFn: () => fetchOrganizationLicenses(orgId!),
    staleTime: 30 * 1000,
  })
}

async function fetchAdminOrganizationMembers(orgId: string): Promise<AdminOrganizationMember[]> {
  const { data, error } = await supabase
    .from('organization_members')
    .select('id, user_id, role, joined_at')
    .eq('org_id', orgId)
    .is('deleted_at', null)
    .order('role', { ascending: false })
    .order('joined_at', { ascending: true })

  if (error) throw new Error(`Failed to fetch organization members: ${error.message}`)
  return (data ?? []) as AdminOrganizationMember[]
}

export function useAdminOrganizationMembers(orgId: string | null) {
  const { isAdmin, isLoading: adminLoading } = useIsAdmin()

  return useQuery({
    queryKey: ['admin', 'organizationMembers', orgId],
    enabled: isAdmin && !adminLoading && !!orgId,
    queryFn: () => fetchAdminOrganizationMembers(orgId!),
    staleTime: 30 * 1000,
  })
}

/**
 * Mutation to update a user license (admin only)
 */
export function useUpdateUserLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      licenseId,
      updates,
    }: {
      licenseId: string
      updates: Partial<Pick<UserLicense, 'annual_quota_km2' | 'total_devices' | 'status' | 'org_id'>>
    }) => {
      const { data, error } = await supabase.from('user_licenses').update(updates).eq('id', licenseId).select().single()

      if (error) {
        throw new Error(`Failed to update license: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'userLicenses'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizationLicenses'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['license-entitlements'] })
    },
  })
}

/**
 * Mutation to create a new user license (admin only)
 */
export function useCreateUserLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (license: {
      user_id: string
      license_tier: string
      total_devices: number
      annual_quota_km2: number
      org_id?: string | null
    }) => {
      const { data, error } = await supabase
        .from('user_licenses')
        .insert({
          ...license,
          expires_at: '2100-01-01T00:00:00Z',
          status: 'active',
        })
        .select()
        .single()

      if (error) {
        throw new Error(`Failed to create license: ${error.message}`)
      }

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'userLicenses'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizationLicenses'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['license-entitlements'] })
    },
  })
}

/**
 * Mutation to revoke a device license (admin only)
 */
export function useRevokeDeviceLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deviceId: string) => {
      const { error } = await supabase.from('device_licenses').update({ status: 'revoked' }).eq('id', deviceId)

      if (error) {
        throw new Error(`Failed to revoke device: ${error.message}`)
      }

      return { success: true, deviceId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDevices'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['license-entitlements'] })
    },
  })
}

/**
 * Mutation to delete a device license (admin only)
 * Hard-deletes the row so the hardware_fingerprint can re-activate.
 */
export function useDeleteDeviceLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (deviceId: string) => {
      const { error } = await supabase.from('device_licenses').delete().eq('id', deviceId)

      if (error) {
        throw new Error(`Failed to delete device license: ${error.message}`)
      }

      return { success: true, deviceId }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'userDevices'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['license-entitlements'] })
    },
  })
}

/**
 * Mutation to toggle admin status for a user (admin only)
 */
export function useToggleAdminStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) => {
      const { error } = await supabase.from('user_profiles').update({ is_admin: isAdmin }).eq('id', userId)

      if (error) {
        throw new Error(`Failed to update admin status: ${error.message}`)
      }

      return { success: true, userId, isAdmin }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'currentProfile'] })
    },
  })
}

/**
 * Mutation to toggle beta tester enrollment for a user (admin only)
 */
export function useToggleBetaTesterStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ userId, isBetaTester }: { userId: string; isBetaTester: boolean }) => {
      const { error } = await supabase.from('user_profiles').update({ is_beta_tester: isBetaTester }).eq('id', userId)

      if (error) {
        throw new Error(`Failed to update beta tester status: ${error.message}`)
      }

      return { success: true, userId, isBetaTester }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'currentProfile'] })
    },
  })
}

/**
 * Format date for display
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format date with time for display (Supabase-style: "10 Sep, 2025 15:01")
 */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-'
  const date = new Date(dateString)
  const day = date.getDate()
  const month = date.toLocaleDateString('en-US', { month: 'short' })
  const year = date.getFullYear()
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${day} ${month}, ${year} ${hours}:${minutes}`
}

/**
 * Format relative time for last seen
 */
export function formatLastSeen(lastSeen: string | null): string {
  if (!lastSeen) return 'Never'

  const now = new Date()
  const seen = new Date(lastSeen)
  const diffMs = now.getTime() - seen.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return `${Math.floor(diffDays / 365)} years ago`
}
